import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ShoppingBag,
  Facebook,
  Copy,
  ExternalLink,
  CheckCircle2,
  Clock,
  BarChart3,
  Loader2,
  Info,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet } from '../lib/api'

/**
 * Página admin /feeds — gestor de feeds de marketing.
 *
 * Expone las URLs públicas de los feeds que consumen Google Merchant Center
 * y Meta Commerce Manager. Los feeds en sí viven en /apps/web/app/feeds/.
 *
 * El admin sólo necesita:
 *   1. Copiar la URL del feed
 *   2. Pegarla en la plataforma destino (GMC / Meta)
 *   3. Ver cuántos productos están en cada feed
 *
 * El sitio público (mazautopartes.com.mx) sirve los feeds desde Next.js;
 * en local apuntamos al mismo origin del web (no del admin).
 */

const SITE_URL = (() => {
  // 1) Prod / staging: usar Sitio_URL configurado (lo expone /api/site-info).
  // 2) Dev: fallback al hostname público actual.
  // Inicializamos con env var de Vite si existe, si no fallback.
  const fromEnv = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_PUBLIC_SITE_URL
  return fromEnv || 'https://mazautopartes.com.mx'
})()

interface ProductosResponse {
  total: number
  page: number
  pageSize: number
  pages: number
}

export function Feeds() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // Total filtrado: is_active=true (lo que reporta /api/admin/productos).
  // Usamos limit=1 para que el response sea liviano — sólo necesitamos `total`.
  const { data: stats, isLoading: statsLoading } = useQuery<ProductosResponse>({
    queryKey: ['feeds-stats'],
    queryFn: () => apiGet('/api/admin/productos?page=1'),
  })

  const totalActivos = stats?.total ?? 0

  function copy(url: string, key: string) {
    navigator.clipboard.writeText(url).catch(() => {})
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const googleUrl = `${SITE_URL}/feeds/google-shopping.xml`
  const facebookUrl = `${SITE_URL}/feeds/facebook-catalog.csv`

  return (
    <>
      <Topbar
        title="Feeds de marketing"
        subtitle="Publica tu catálogo en Google Shopping y Facebook Catalog"
      />

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Google Shopping */}
          <FeedCard
            title="Google Shopping"
            badge="XML"
            color="emerald"
            icon={<ShoppingBag className="size-5" />}
            description="Feed XML estándar para Google Merchant Center. Se actualiza cada hora con productos activos y disponibles."
            url={googleUrl}
            copiedKey={copiedKey}
            keyId="google"
            onCopy={() => copy(googleUrl, 'google')}
            instructions={
              <ol className="ml-4 list-decimal space-y-1 text-xs text-ink-600">
                <li>
                  Abre{' '}
                  <a
                    href="https://merchants.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-brand-600 hover:underline"
                  >
                    Google Merchant Center
                  </a>
                </li>
                <li>Ve a <span className="font-mono">Productos → Feeds → Agregar feed</span></li>
                <li>Selecciona "Recuperación programada" y pega la URL de arriba</li>
                <li>Frecuencia recomendada: diaria</li>
              </ol>
            }
          />

          {/* Facebook Catalog */}
          <FeedCard
            title="Facebook Catalog"
            badge="CSV"
            color="blue"
            icon={<Facebook className="size-5" />}
            description="Feed CSV para Meta Commerce Manager (Facebook + Instagram Shopping). Mismo dataset que Google, formato Meta."
            url={facebookUrl}
            copiedKey={copiedKey}
            keyId="facebook"
            onCopy={() => copy(facebookUrl, 'facebook')}
            instructions={
              <ol className="ml-4 list-decimal space-y-1 text-xs text-ink-600">
                <li>
                  Abre{' '}
                  <a
                    href="https://business.facebook.com/commerce"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-brand-600 hover:underline"
                  >
                    Meta Commerce Manager
                  </a>
                </li>
                <li>Ve a <span className="font-mono">Catálogo → Fuentes de datos → Agregar productos</span></li>
                <li>Elige "Usar feed programado" y pega la URL de arriba</li>
                <li>Frecuencia recomendada: diaria</li>
              </ol>
            }
          />
        </div>

        {/* Estadísticas */}
        <div className="mt-6 rounded-2xl border border-ink-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600">
              <BarChart3 className="size-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink-900">Estadísticas</h2>
              <p className="text-xs text-ink-500">Productos incluidos en cada feed</p>
            </div>
          </div>

          {statsLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-500">
              <Loader2 className="size-4 animate-spin" />
              Calculando…
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatBox
                label="Productos activos en BD"
                value={totalActivos.toLocaleString('es-MX')}
                hint="is_active = true"
              />
              <StatBox
                label="Google Shopping"
                value={totalActivos.toLocaleString('es-MX')}
                hint="con stock > 0 (default)"
                badge={<Badge variant="success">activo</Badge>}
              />
              <StatBox
                label="Facebook Catalog"
                value={totalActivos.toLocaleString('es-MX')}
                hint="con stock > 0 (default)"
                badge={<Badge variant="success">activo</Badge>}
              />
            </div>
          )}

          <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <Info className="mt-0.5 size-4 flex-none" />
            <div>
              Los feeds excluyen productos sin stock por defecto. Para incluirlos, agrega{' '}
              <span className="font-mono">?includeOutOfStock=1</span> a la URL del feed. Las
              plataformas marcarán esos items como <span className="font-mono">out of stock</span>.
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

/* ---------- componentes auxiliares ---------- */

interface FeedCardProps {
  title: string
  badge: string
  color: 'emerald' | 'blue'
  icon: React.ReactNode
  description: string
  url: string
  copiedKey: string | null
  keyId: string
  onCopy: () => void
  instructions: React.ReactNode
}

function FeedCard({
  title,
  badge,
  color,
  icon,
  description,
  url,
  copiedKey,
  keyId,
  onCopy,
  instructions,
}: FeedCardProps) {
  const copied = copiedKey === keyId
  const colorClasses =
    color === 'emerald'
      ? { iconBg: 'bg-emerald-50 text-emerald-600', badgeBg: 'bg-emerald-100 text-emerald-800' }
      : { iconBg: 'bg-blue-50 text-blue-600', badgeBg: 'bg-blue-100 text-blue-800' }

  return (
    <div className="flex flex-col rounded-2xl border border-ink-200 bg-white p-6">
      <div className="mb-3 flex items-center gap-3">
        <div className={`grid size-10 place-items-center rounded-xl ${colorClasses.iconBg}`}>
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
            <span
              className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${colorClasses.badgeBg}`}
            >
              {badge}
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-ink-500">
            <Clock className="size-3" />
            <span>Se actualiza cada hora</span>
          </div>
        </div>
      </div>

      <p className="mb-4 text-sm text-ink-600">{description}</p>

      <div className="mb-4">
        <label className="mb-1 block text-xs font-semibold text-ink-700">URL pública del feed</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={url}
            readOnly
            onFocus={(e) => e.target.select()}
            className="flex-1 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 font-mono text-xs text-ink-800 outline-none"
          />
          <button
            type="button"
            onClick={onCopy}
            title="Copiar URL"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
              copied
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-100'
            }`}
          >
            {copied ? (
              <>
                <CheckCircle2 className="size-3.5" />
                Copiada
              </>
            ) : (
              <>
                <Copy className="size-3.5" />
                Copiar
              </>
            )}
          </button>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Ver feed en una pestaña nueva"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-medium text-ink-700 hover:bg-ink-100"
          >
            <ExternalLink className="size-3.5" />
            Ver feed
          </a>
        </div>
      </div>

      <div className="mt-auto rounded-xl border border-ink-200 bg-ink-50 p-3">
        <div className="mb-2 text-xs font-semibold text-ink-900">Cómo conectarlo</div>
        {instructions}
      </div>
    </div>
  )
}

function StatBox({
  label,
  value,
  hint,
  badge,
}: {
  label: string
  value: string
  hint?: string
  badge?: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-ink-200 p-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-ink-700">{label}</span>
        {badge}
      </div>
      <div className="text-2xl font-bold text-ink-900">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-ink-500">{hint}</div>}
    </div>
  )
}
